import { ErrorHandler, inject, Injectable } from '@angular/core';
import {
    ApplicationInsights,
    IConfig,
    IConfiguration,
    ICustomProperties,
    IExceptionTelemetry,
    ITelemetryItem,
    ITraceTelemetry,
} from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { Router } from '@angular/router';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { AppInsightsAngularPluginConfig, AppInsightsClickPluginConfig, redactString } from './pace-insights.conf';
import { truncateAppInsightsProperties } from './pace-insights.utils';

@Injectable({
    providedIn: 'root',
})
export class PaceInsightsService {
    private readonly router = inject(Router);
    private appInsights?: ApplicationInsights;
    private insightsEnabled = false;
    private readonly angularPluginInstance = inject(AngularPlugin);
    private appVersion = 'unknown';
    private host: string | undefined;
    private gid: string | undefined;

    public init(connectionString: string): void {
        this.insightsEnabled = !!connectionString;
        if (this.insightsEnabled) {
            this.host = document.location.host;

            const clickPluginInstance = new ClickAnalyticsPlugin();

            const angularPluginConfig: IConfiguration & IConfig = {
                ...AppInsightsAngularPluginConfig,

                connectionString,

                extensions: [this.angularPluginInstance, clickPluginInstance],
                extensionConfig: {
                    [this.angularPluginInstance.identifier]: {
                        router: this.router,
                        errorServices: [new ErrorHandler()],
                    },
                    [clickPluginInstance.identifier]: AppInsightsClickPluginConfig,
                },
            };

            this.appInsights = new ApplicationInsights({
                config: angularPluginConfig,
            });

            let exposedSession = false;
            this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
                if (!exposedSession) {
                    console.log(this.getClientSession());
                    exposedSession = true;
                }

                envelope.data = envelope.data || {};
                envelope.data = {
                    ...envelope.data,
                    appVersion: this.appVersion,
                    paceWebHost: this.host,
                    // The four correlation scopes, widest to narrowest, under the same names
                    // pace-fx-app binds — so one customDimensions filter spans both sides.
                    gid: this.gid,
                    sessionId: this.getSessionId(),
                    traceId: this.getTraceId(),
                    spanId: this.getSpanId(envelope),
                };

                envelope.data = this.redactSensitiveInformation(envelope.data);
                envelope.ext = this.redactSensitiveInformation(envelope.ext);
                envelope.baseData = this.redactSensitiveInformation(envelope.baseData);

                envelope.tags = envelope.tags || {};
                envelope.tags['ai.cloud.role'] = 'pace-web';
                // ai.operation.id is deliberately left to the SDK: it sets it to the trace-id
                // it also sends in the outgoing traceparent header, which pace-fx-app extracts
                // as its traceId. Overwriting it with a session-wide id (as this did) made
                // client and server rows unjoinable and broke the portal's end-to-end
                // transaction view. Session-wide grouping is customDimensions.sessionId.
            });

            this.appInsights.loadAppInsights();
        }
    }

    private redactSensitiveInformation(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
        if (!properties || properties.length === 0) {
            return properties;
        }
        for (const key of Object.keys(properties || {})) {
            if (typeof properties[key] === 'string') {
                properties[key] = redactString(properties[key]);
            }
        }
        return properties;
    }

    public isInsightsEnabled(): boolean {
        return this.insightsEnabled;
    }

    public getClientSession(): string {
        return `Session: ${this.getSessionId() ?? 'unknown'}`;
    }

    /** Widest client-side scope: survives reloads and route changes, resets with the browser session. */
    public getSessionId(): string | undefined {
        return this.appInsights?.context.getSessionId();
    }

    /**
     * One view's worth of calls — the SDK rotates this on route change/reload and sends it as
     * the trace-id in the outgoing traceparent, which pace-fx-app binds as its own traceId.
     * Returned as-is: W3C trace-ids are lowercase hex and the server stores the header value
     * verbatim, so upper-casing would stop a quoted support id from matching server-side.
     */
    public getTraceId(): string {
        return this.appInsights?.context?.telemetryTrace?.traceID ?? '';
    }

    /**
     * Narrowest scope: one HTTP call. Only dependency telemetry has one — the browser SDK keeps
     * no ambient span context, so a UI-level log statement genuinely belongs to no single call.
     *
     * The SDK writes dependency ids in the legacy hierarchical form `|<traceId>.<spanId>`
     * (see ajaxRecord's `id` assignment), so the bare span id has to be pulled back out —
     * otherwise it would never match what pace-fx-app reads from the traceparent header.
     */
    private getSpanId(envelope: ITelemetryItem): string | undefined {
        if (envelope.baseType !== 'RemoteDependencyData') {
            return undefined;
        }
        const dependencyId = envelope.baseData?.id as string | undefined;
        return dependencyId?.replace(/^\|/, '').split('.')[1];
    }

    public setAppVersion(appVersion: string): void {
        this.appVersion = appVersion;
    }

    public async setUser(gid?: string, country?: string, salesOrg?: string, plant?: string): Promise<void> {
        gid = gid || 'unknown';
        this.gid = gid;
        this.appInsights?.setAuthenticatedUserContext(gid, `${country}::${salesOrg}::${plant}`, true);
        this.trackEvent('user logged in', { gid, country, salesOrg, plant });
    }

    public trackEvent(name: string, properties?: ICustomProperties): void {
        this.appInsights?.trackEvent({ name }, truncateAppInsightsProperties(properties));
    }

    public trackTrace(trace: ITraceTelemetry, customProperties?: ICustomProperties): void {
        this.appInsights?.trackTrace(trace, truncateAppInsightsProperties(customProperties));
    }

    public trackException(error: IExceptionTelemetry, props?: ICustomProperties): void {
        this.appInsights?.trackException(error, truncateAppInsightsProperties(props));
    }

    public trackDependency(dependency: {
        id: string;
        target?: string;
        name?: string;
        data?: string;
        duration?: number;
        responseCode: number;
        success?: boolean;
        type?: string;
    }): void {
        this.appInsights?.trackDependencyData(dependency);
    }

    public trackPage(name?: string, uri?: string): void {
        this.appInsights?.trackPageView({ name, uri });
    }
}
